import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { Header } from '../components/Header';
import { AdminBottomNavigation } from '../components/AdminBottomNavigation';
import { AdminSidebar } from '../components/AdminSidebar';
import { AdminDesktopSidebar } from '../components/AdminDesktopSidebar';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { PostService } from '../services/postService';
import { Post, ThreadNote } from '../types/index';
import { AppColors } from '../theme/colors';
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';

// 日時フォーマット関数
const toDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  return new Date(timestamp);
};

const formatDateTime = (date: Date): string => {
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const VoiceDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // スレッドメモ関連
  const [threadNotes, setThreadNotes] = useState<ThreadNote[]>([]);
  const [noteContent, setNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  // 投稿を取得
  const fetchPost = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    try {
      const fetchedPost = await PostService.getPostForAdmin(postId);
      if (fetchedPost) {
        setPost(fetchedPost);
      } else {
        setError('投稿が見つかりません');
      }
    } catch (err) {
      console.error('投稿取得エラー:', err);
      setError('投稿を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  // スレッドメモをリアルタイム購読
  useEffect(() => {
    if (!postId) return;

    setNotesLoading(true);
    const q = fsQuery(
      collection(db, 'threadNotes'),
      where('postId', '==', postId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notes: ThreadNote[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          postId: doc.data().postId,
          content: doc.data().content,
          createdBy: doc.data().createdBy,
          createdByEmail: doc.data().createdByEmail,
          createdByName: doc.data().createdByName,
          createdAt: doc.data().createdAt,
        } as ThreadNote));
        setThreadNotes(notes);
        setNotesLoading(false);
      },
      (error) => {
        console.error('スレッドメモ取得エラー:', error);
        setNotesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [postId]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  // スレッドメモを追加
  const handleAddThreadNote = async () => {
    if (!postId || !noteContent.trim() || !user) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await PostService.addThreadNote(postId, noteContent, user.uid, user.email, user.displayName);
      setNoteContent('');
      // リアルタイム購読により自動的に更新されるため、fetchThreadNotes()は不要
    } catch (err) {
      console.error('スレッドメモ追加エラー:', err);
      setSubmitError((err as Error).message || 'メモの投稿に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enterキーでメモを送信（Ctrl+Enter または Cmd+Enter）
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!isSubmitting && noteContent.trim()) {
        handleAddThreadNote();
      }
    }
  };

  // ステータス変更
  const handleChangeStatus = async (newStatus: 'pending' | 'accepted' | 'in_progress' | 'completed') => {
    if (!post || !user || !company) return;
    
    try {
      const postRef = doc(db, 'posts', post.postId);
      const snap = await getDoc(postRef);
      const data = (snap.data() as Record<string, unknown>) || {};
      const oldStatus: string = (data as { status?: string }).status || 'unknown';
      const postContent: string = (data as { content?: string }).content || '';

      // ステータスラベルの取得
      const getStatusLabel = (status: string): string => {
        switch (status) {
          case 'accepted': return '受理';
          case 'in_progress': return '対応中';
          case 'completed': return '完了';
          case 'pending': default: return '未処理';
        }
      };

      // 更新
      const update: Record<string, unknown> = {
        status: newStatus,
        updatedAt: serverTimestamp(),
      };
      if (newStatus === 'completed') {
        update.completedAt = serverTimestamp();
        update.completedBy = user?.uid || null;
      }
      await updateDoc(postRef, update);

      // 監査ログ
      if (user?.uid && company?.id) {
        await addDoc(collection(db, 'audit_logs'), {
          action: 'voice',
          description: `${getBoxTemplate(company?.industryType, 'voice').name}ステータス変更 [${getStatusLabel(oldStatus)}] → [${getStatusLabel(newStatus)}]`,
          userId: user.uid,
          userName: user.displayName || '管理者',
          companyId: company.id,
          timestamp: serverTimestamp(),
          details: {
            postId: post.postId,
            postType: 'voice',
            // プレビューは全文を保存（監査ログ表示側で短縮を制御）
            postContentPreview: postContent,
            oldStatus,
            newStatus,
            oldStatusLabel: getStatusLabel(oldStatus),
            newStatusLabel: getStatusLabel(newStatus),
            actionType: 'status_change',
          },
        });
      }

      // 投稿データを再取得
      fetchPost();
    } catch (e) {
      console.error('ステータス変更に失敗しました', e);
    } finally {
      setStatusDialogOpen(false);
    }
  };

  if (authLoading || companyLoading || loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Header 
          onMenuClick={() => setDrawerOpen(true)}
          onSettingsClick={() => navigate('/settings')}
          showAdminBadge={true}
        freeTrialEndDate={company?.isFreeTrial ? company.freeTrialEndDate : undefined}
        companyName={company?.companyName}
        corporateCode={company?.corporateCode}
        />
        
        {/* モバイル用サイドバー */}
        {!isDesktop && <AdminSidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

        {/* レイアウトコンテナ */}
        <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
          {/* PC版用固定サイドバー */}
          {isDesktop && <AdminDesktopSidebar />}

        {/* メインコンテンツ */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          pb: isDesktop ? 0 : 10,
          marginLeft: isDesktop ? '240px' : 0,
        }}>
          {/* 中ヘッダー（他ページと同構造） */}
          <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin?tab=voice')}
                sx={{ 
                  color: AppColors.textPrimary,
                  mr: 2,
                  minWidth: 'auto',
                  p: 0.5
                }}
              />
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
                投稿詳細
              </Typography>
            </Box>
          </Box>

          {/* 標準の余白の状態用 内側コンテナ */}
          <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%', p: 2 }}>
          <Alert severity="error">{error || '投稿が見つかりません'}</Alert>
          <Button variant="contained" onClick={() => navigate('/admin')} sx={{ mt: 2 }}>
            戻る
          </Button>
        </Box>
          </Box>
        </Box>

        {/* モバイル版のみボトムナビゲーション表示 */}
        {!isDesktop && (
          <AdminBottomNavigation 
            currentTab="voice"
            onTabChange={(tab) => {
              if (tab === 'theme') {
                navigate('/admin/theme-posts');
              } else if (tab === 'voice') {
                navigate('/admin?tab=voice');
              } else if (tab === 'thanks') {
                navigate('/admin?tab=thanks');
              }
            }}
          />
        )}
      </Box>
    );
  }

  // AdminPageと同じ関数を定義
  const voiceStatusColor = (status: string): string => {
    switch (status) {
      case 'accepted':
        return '#1976d2'; // blue
      case 'in_progress':
        return '#ed6c02'; // orange
      case 'completed':
        return '#2e7d32'; // green
      case 'pending':
      default:
        return '#9e9e9e'; // grey
    }
  };

  const voiceStatusLabel = (status: string): string => {
    switch (status) {
      case 'accepted':
        return '受理';
      case 'in_progress':
        return '対応中';
      case 'completed':
        return '完了';
      case 'pending':
      default:
        return '未処理';
    }
  };

  const voiceStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleOutlineIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'in_progress':
        return <WorkOutlineIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'pending':
      default:
        return <PendingIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
    }
  };

  const status = post.status || 'pending';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header 
        onMenuClick={() => setDrawerOpen(true)}
        onSettingsClick={() => navigate('/settings')}
        showAdminBadge={true}
        freeTrialEndDate={company?.isFreeTrial ? company.freeTrialEndDate : undefined}
        companyName={company?.companyName}
        corporateCode={company?.corporateCode}
      />

      {/* モバイル用サイドバー */}
      {!isDesktop && <AdminSidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

      {/* レイアウトコンテナ（ヘッダーの高さ分のpaddingを追加） */}
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* PC版用固定サイドバー */}
        {isDesktop && <AdminDesktopSidebar />}

        {/* メインコンテンツ */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          pb: isDesktop ? 0 : 10,
          marginLeft: isDesktop ? '240px' : 0,
        }}>
          {/* 中ヘッダー */}
          <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin?tab=voice')}
                sx={{ 
                  color: AppColors.textPrimary,
                  mr: 2,
                  minWidth: 'auto',
                  p: 0.5
                }}
              />
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
                投稿詳細
              </Typography>
            </Box>
        </Box>

          {/* 投稿詳細 */}
          <Box sx={{ 
            flex: 1, 
            p: 2,
            maxWidth: isDesktop ? '1000px' : '100%',
            mx: isDesktop ? 'auto' : 0,
            width: '100%',
          }}>

        {/* 投稿詳細 */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {/* ステータスバッジ */}
            <Box sx={{ mb: 2 }}>
              <Box
                onClick={() => setStatusDialogOpen(true)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1.5,
                  border: `1px solid ${voiceStatusColor(status)}`,
                  bgcolor: voiceStatusColor(status) + '20',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {voiceStatusIcon(status)}
                <Typography sx={{ ml: 0.5, fontSize: 12, fontWeight: 700, color: voiceStatusColor(status) }}>
                  {voiceStatusLabel(status)}
                </Typography>
              </Box>
            </Box>

            {/* 投稿内容 */}
            <Typography sx={{ fontSize: 16, lineHeight: 1.6, color: AppColors.textPrimary, mb: 2, whiteSpace: 'pre-line' }}>
              {post.processedContent || post.content}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* 投稿情報 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: AppColors.textSecondary }} />
                <Typography sx={{ fontSize: 13, color: AppColors.textSecondary }}>
                  投稿日時: {formatDateTime(toDate(post.createdAt))}
                </Typography>
              </Box>
              {post.tags && post.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {post.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={`#${tag}`}
                      size="small"
                      sx={{
                        bgcolor: AppColors.primary + '20',
                        color: AppColors.primary,
                        fontSize: 11,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* スレッドメモセクション */}
        <Card sx={{ mb: 1.5, borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black', mb: 2 }}>
              メモを書く
            </Typography>

            {/* メモ入力フォーム */}
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="メモを入力してください..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ mb: 1 }}
              />
              {submitError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {submitError}
                </Alert>
              )}
              <Box sx={{ width: '100%', mb: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddThreadNote}
                  disabled={!noteContent.trim() || isSubmitting}
                >
                  保存
                </Button>
              </Box>
            </Box>

            {/* メモ一覧 */}
            {notesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : threadNotes.length === 0 ? null : (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {threadNotes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                      border: `1px solid ${AppColors.border}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: AppColors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'white',
                            fontSize: 14,
                            fontWeight: 'bold',
                          }}
                        >
                          {(note.createdByName || note.createdByEmail || '管理者').charAt(0).toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: AppColors.textPrimary }}>
                            {note.createdByName || note.createdByEmail || '管理者'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: AppColors.textSecondary, ml: 1 }}>
                            {formatDateTime(toDate(note.createdAt))}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, color: AppColors.textPrimary, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
          </Box>
        </Box>
      </Box>

      {/* モバイル版のみボトムナビゲーション表示 */}
      {!isDesktop && (
      <AdminBottomNavigation 
        currentTab="voice"
        onTabChange={(tab) => {
          if (tab === 'theme') {
            navigate('/admin/theme-posts');
          } else if (tab === 'voice') {
            navigate('/admin?tab=voice');
          } else if (tab === 'thanks') {
            navigate('/admin?tab=thanks');
          }
        }}
      />
      )}

      {/* ステータス変更ダイアログ */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 260 }}>
            <ListItemButton onClick={() => handleChangeStatus('pending')}>
              <ListItemIcon>
                <PendingIcon sx={{ color: '#9e9e9e' }} />
              </ListItemIcon>
              <ListItemText primary="未処理" primaryTypographyProps={{ sx: { color: '#9e9e9e', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => handleChangeStatus('accepted')}>
              <ListItemIcon>
                <CheckCircleOutlineIcon sx={{ color: '#1976d2' }} />
              </ListItemIcon>
              <ListItemText primary="受理" primaryTypographyProps={{ sx: { color: '#1976d2', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => handleChangeStatus('in_progress')}>
              <ListItemIcon>
                <WorkOutlineIcon sx={{ color: '#ed6c02' }} />
              </ListItemIcon>
              <ListItemText primary="対応中" primaryTypographyProps={{ sx: { color: '#ed6c02', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => handleChangeStatus('completed')}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#2e7d32' }} />
              </ListItemIcon>
              <ListItemText primary="完了" primaryTypographyProps={{ sx: { color: '#2e7d32', fontWeight: 600 } }} />
            </ListItemButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>キャンセル</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

